// Pure event helpers — schedule layout math and template summarization.
//
// `buildScheduleSummary` paints a live "총 N시간 = 웜업 W + R × (경기 M + 휴게 B)"
// hint into the new-event sheet, recomputing on any input change. The
// element flips into a warning/error state when the requested rounds don't
// fit (or when the per-match minute is suspiciously short).
//
// `buildOvernightBadge` shows a "(+1 다음날)" pill when the end-time crosses
// midnight relative to the start-time. `buildDateTimes` mirrors that logic
// for ISO assembly. `formatTemplateSummary` renders the small caption line
// shown beneath each saved event template.
//
// All four are leaf utilities — no state, no api, no router. Heavier event
// flows (renderEvents, openNewEventSheet, etc.) still live in legacy.ts.
//
// The time/round controls passed in are the small `{ getValue, onChange,
// setValue }` objects produced by buildTimePicker / buildNumberStepper —
// not native inputs. Their full shape stays in legacy until those builders
// are themselves extracted.

import { h } from "../ui/dom";
import { DAY_LABELS } from "../ui/format";

interface ValueControl<T> {
  getValue: () => T;
  onChange: (cb: () => void) => void;
}

type TimeControl = ValueControl<string>;
type NumberControl = ValueControl<number>;

export function buildScheduleSummary(
  startTime: TimeControl,
  endTime: TimeControl,
  rounds: NumberControl,
  matchMin: NumberControl,
  breakMin: NumberControl,
  warmupMin: NumberControl | null | undefined,
): HTMLElement {
  const el = h("div", { class: "schedule-summary" }, "");
  function update() {
    const [sh, sm] = startTime.getValue().split(":").map(Number);
    const [eh, em] = endTime.getValue().split(":").map(Number);
    let total = (eh * 60 + em) - (sh * 60 + sm);
    if (total <= 0) total += 24 * 60;
    const r = rounds.getValue();
    const m = matchMin.getValue();
    const b = breakMin.getValue();
    const w = warmupMin ? warmupMin.getValue() : 0;
    const needed = w + r * (m + b) - b;
    const h_ = Math.floor(total / 60);
    const mm_ = total % 60;
    const totalStr = h_ > 0 ? (mm_ > 0 ? `${h_}시간 ${mm_}분` : `${h_}시간`) : `${mm_}분`;
    el.textContent = `총 ${totalStr} = 웜업 ${w}분 + ${r}R × (경기 ${m}분 + 휴게 ${b}분)`;
    el.classList.remove("schedule-summary--warn", "schedule-summary--error");
    if (needed > total) el.classList.add("schedule-summary--error");
    else if (m <= 25) el.classList.add("schedule-summary--warn");
  }
  startTime.onChange(update);
  endTime.onChange(update);
  rounds.onChange(update);
  matchMin.onChange(update);
  breakMin.onChange(update);
  if (warmupMin) warmupMin.onChange(update);
  update();
  return el;
}

export function buildOvernightBadge(
  startTime: TimeControl,
  endTime: TimeControl,
): HTMLElement {
  const badge = h("span", { class: "overnight-badge", hidden: true }, " (+1 다음날)") as HTMLElement;
  function update() {
    const s = startTime.getValue();
    const e = endTime.getValue();
    badge.hidden = !(e <= s);
  }
  startTime.onChange(update);
  endTime.onChange(update);
  update();
  return badge;
}

// Build ISO datetime strings, handling overnight (end <= start → next day).
export function buildDateTimes(
  date: string,
  startHHMM: string,
  endHHMM: string,
): { startISO: string; endISO: string } {
  const startISO = `${date}T${startHHMM}:00`;
  let endDate = date;
  if (endHHMM <= startHHMM) {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + 1);
    const pad = (n: number) => String(n).padStart(2, "0");
    endDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
  const endISO = `${endDate}T${endHHMM}:00`;
  return { startISO, endISO };
}

export function formatTemplateSummary(t: any): string {
  const parts: string[] = [];
  if (t.day_of_week !== null && t.day_of_week !== undefined) {
    parts.push(`${DAY_LABELS[t.day_of_week]}요일`);
  }
  if (t.start_time && t.end_time) parts.push(`${t.start_time}–${t.end_time}`);
  if (t.location) parts.push(t.location);
  return parts.join(" · ");
}
